import React, { FC } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { DEMO_STAY_LISTINGS } from "data/listings";
import { StayDataType } from "data/types";
import StartRating from "components/StartRating/StartRating";
import { Link } from "react-router-dom";
import BtnLikeIcon from "components/BtnLikeIcon/BtnLikeIcon";
import Badge from "shared/Badge/Badge";
import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import { currencySymbol } from "shared/CurrencySymbol";
import ButtonPrimary from "shared/Button/ButtonPrimary";

export interface StayCardHProps {
  className?: string;
  data?: StayDataType;
}

const DEMO_DATA = DEMO_STAY_LISTINGS[0];

const StayCardH: FC<StayCardHProps> = ({
  className = "",
  data = DEMO_DATA,
}) => {
  const {
    title,
    href = data.href || `/listing-stay-detail/${data?.slug}/?navigatedFrom=search`,
    isAds,
    price_per_month: price,
    average,
    id,
    city,
    state,
    neighborhood,  // ✅ ADD THIS
    place_images,
    total_bathrooms,
    total_bedrooms,
    total_rooms,
    count,
    next_available_date,
    is_favorite,
    is_featured,
    is_verified,
  } = data;

  
  const PropertyStatus = data.is_shared_property;
  
  const modifiedPropertyStatus = (PropertyStatus: string | undefined | boolean) => {
    const status = typeof PropertyStatus === "boolean" ? PropertyStatus.toString() : PropertyStatus;
    switch (status) {
      case "true":
        return "Shared Property";
      case "false":
        return "Entire Property";
      default:
        return "";
    }
  };

  const { currentUser } = useSelector((state: RootState) => state.user);
  
  // ✅ UPDATED: Include neighborhood in address
  const shortAddress = [
    neighborhood,
    city,
    state
  ].filter(Boolean).join(', ');

  const renderSliderGallery = () => {
    return (
      <div className="relative flex-shrink-0 w-full md:w-72">
        <GallerySlider
          ratioClass="aspect-w-6 aspect-h-5"
          galleryImgs={place_images}
          uniqueID={`StayCardH_${id}`}
          href={href}
        />
        
        {/* Featured Badge - Top Left */}
        {is_featured && (
          <div className="absolute top-3 left-3 z-10">
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500 text-white shadow-lg">
              ⭐ Featured
            </span>
          </div>
        )}

        {/* Verified Badge - Bottom Left */}
        {is_verified && (
          <div className="absolute bottom-3 left-3 z-10">
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500 text-white shadow-lg">
              ✓ Verified
            </span>
          </div>
        )}

        {/* Like Button */}
        {currentUser?.is_student && (
          <BtnLikeIcon
            isLiked={is_favorite}
            className="absolute right-3 top-3 z-10"
            propertyId={typeof data?.id === "number" ? data.id : undefined}
            path="propertySearch"
          />
        )}
      </div>
    );
  };

  const renderAmenities = () => {
    const amenities = [];
    
    if (total_rooms) {
      amenities.push({
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18" />
          </svg>
        ),
        label: `${total_rooms} ${total_rooms === 1 ? "room" : "rooms"}`
      });
    }

    if (total_bedrooms) {
      amenities.push({
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 4v16" />
            <path d="M2 8h18a2 2 0 0 1 2 2v10" />
            <path d="M2 17h20" />
            <path d="M6 8v9" />
          </svg>
        ),
        label: `${total_bedrooms} ${total_bedrooms === 1 ? "bedroom" : "bedroom"}`
      });
    }

    if (total_bathrooms) {
      amenities.push({
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
            <line x1="10" x2="8" y1="5" y2="7" />
            <line x1="2" x2="22" y1="12" y2="12" />
            <line x1="7" x2="7" y1="19" y2="21" />
            <line x1="17" x2="17" y1="19" y2="21" />
          </svg>
        ),
        label: `${total_bathrooms} ${total_bathrooms === 1 ? "bath" : "baths"}`
      });
    }

    if (amenities.length === 0) return null;

    return (
      <div className="flex items-center flex-wrap gap-3 sm:gap-4">
        {amenities.map((amenity, index) => (
          <React.Fragment key={index}>
            <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
              <div className="text-gray-500 dark:text-gray-400">
                {amenity.icon}
              </div>
              <span className="text-sm font-medium whitespace-nowrap">
                {amenity.label}
              </span>
            </div>
            {index < amenities.length - 1 && (
              <div className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600 hidden sm:block" />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="flex-grow p-3 sm:p-4 flex flex-col justify-between">
        <div className="space-y-2">
          {/* Badges Row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {isAds && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
                ADS
              </span>
            )}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200 dark:bg-primary-900/20 dark:text-primary-400 dark:border-primary-800">
              {modifiedPropertyStatus(PropertyStatus) || "Property"}
            </span>
            {(data as any).property_category?.name && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">
                {(data as any).property_category?.name}
              </span>
            )}
          </div>

          {/* Title */}
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white line-clamp-1">
            {title}
          </h2>

          {/* Location - ✅ UPDATED: Now shows neighborhood */}
          <div className="flex items-center gap-2 text-sm">
  <svg className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
  <div className="flex items-center gap-1.5 flex-wrap">
    {neighborhood && (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400">
        {neighborhood}
      </span>
    )}
    <span className="text-xs text-gray-600 dark:text-gray-400">
      {[city, state].filter(Boolean).join(', ')}
    </span>
  </div>
</div>

          {/* Amenities */}
          <div>
            {renderAmenities()}
          </div>

          {/* Available Date Badge - Compact */}
          {next_available_date && (
            <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 w-fit">
              Available {next_available_date}
            </div>
          )}
        </div>

        {/* Bottom Section - Compact Rating and Price */}
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-3">
            {/* Rating */}
            <div className="flex-shrink-0">
              <StartRating reviewCount={average} reviewTotal={count} />
            </div>

            {/* Price - Compact */}
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  From{" "}
                  <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                    {currencySymbol()}{Number(price).toLocaleString()}
                  </span>
                  <span className="text-xs">/mo</span>
                </div>
              </div>
              
              {/* View Button - Compact */}
              <Link to={href} className="relative z-10">
                <button className="px-3 py-1.5 text-xs font-medium text-white bg-primary-6000 hover:bg-primary-700 rounded-lg transition-colors whitespace-nowrap">
                  View
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`nc-StayCardH group relative bg-white dark:bg-neutral-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden hover:shadow-xl hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-300 ${className}`}
      data-nc-id="StayCardH"
    >
      <div className="grid grid-cols-1 md:flex md:flex-row">
        {renderSliderGallery()}
        {renderContent()}
      </div>
    </div>
  );
};

export default StayCardH;